"use client";

import dayjs, { Dayjs } from "dayjs";
import {
  useState,
  useEffect,
  createContext,
  Dispatch,
  SetStateAction,
  ReactNode,
  useContext,
} from "react";
import { useSearchParams } from "next/navigation";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { MultiInputDateRangeField } from "@mui/x-date-pickers-pro/MultiInputDateRangeField";
import { FieldOwnerState } from "@mui/x-date-pickers/models";

export const DateRangeContext = createContext<{
  dateRange: [Dayjs | null, Dayjs | null];
  setDateRange: Dispatch<SetStateAction<[Dayjs | null, Dayjs | null]>>;
}>({
  dateRange: [null, null],
  setDateRange: () => null,
});

export function DateRangeProvider({
  children,
  defaultStartDate = dayjs().subtract(7, "day").valueOf(),
  defaultEndDate,
}: {
  children: ReactNode;
  defaultStartDate?: number;
  defaultEndDate?: number;
}) {
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs(defaultStartDate),
    dayjs(defaultEndDate),
  ]);
  return (
    <DateRangeContext.Provider value={{ dateRange, setDateRange }}>
      {children}
    </DateRangeContext.Provider>
  );
}

export default function CustomDateRangePicker({
  disableFuture = false,
}: {
  disableFuture?: boolean;
}) {
  const searchParams = useSearchParams();
  const { dateRange, setDateRange } = useContext(DateRangeContext);
  useEffect(() => {
    const tempDateRange = searchParams?.getAll("date-range") || [];
    if (tempDateRange.length > 0) {
      const start = tempDateRange[0] ? dayjs(Number(tempDateRange[0])) : null;
      const end = tempDateRange[1] ? dayjs(Number(tempDateRange[1])) : null;
      setDateRange([start, end]);
    }
  }, [searchParams, setDateRange]);

  return (
    <>
      <DateRangePicker
        // this fixes a next js hydration error.
        // Try to find a better solution at some point as this setting will be deprecated at some point
        name="date-range"
        enableAccessibleFieldDOMStructure={false}
        slotProps={{
          textField: ({
            position,
          }: FieldOwnerState & { position?: "start" | "end" }) => {
            return {
              label: position === "start" ? "Start Date" : "End Date",
            };
          },
        }}
        value={dateRange}
        onChange={(newValue) => setDateRange(newValue)}
        slots={{ field: MultiInputDateRangeField }}
        disableFuture={disableFuture}
      />
      <input
        type="hidden"
        name="date-range"
        value={dateRange[0]?.startOf("day").valueOf()}
      />
      <input
        type="hidden"
        name="date-range"
        value={dateRange[1]?.endOf("day").valueOf()}
      />
    </>
  );
}
